import { parseId } from '../../utils'
import coerceArray from '../../utils/coerceArray'
import { uniqBy } from 'lodash-es'
import { Config, UserRole } from '../types'
import useConfig from '../useConfig'
import Roles from './Roles'

export type AccessRole = {
    name: string
    level?: 'com' | 'loc' | 'emp' | 'grp' | 'cli' | 'coc'
    parent?: string | null
}
export type AccessRoles = string | string[] | AccessRole | AccessRole[]
export type AccessRoleParents = Pick<Config, 'employeeId' | 'locationId' | 'companyId' | 'groupId'>

const findParentType = (type: AccessRole['level']) => {
    switch (type) {
        case 'grp':
            return 'groupId'
        case 'com':
        case 'cli':
        case 'coc':    
            return 'companyId'
        case 'loc':
            return 'locationId'
        case 'emp':
            return 'employeeId'
    }
}

const getDefaultRoles = (_parent: string, roles: UserRole[]): any => {
    let defaultRoles: UserRole[] = [...roles]
    const { type, parent } = parseId(_parent)
    switch (type) {
        case 'grp':
            defaultRoles = [...defaultRoles, { name: Roles.GroupLevel, parent: _parent }]
            break
        case 'com':
        case 'cli':
        case 'coc':    
            defaultRoles = [
                ...defaultRoles,
                { name: Roles.GroupLevel, parent },
                { name: Roles.CompanyLevel, parent: _parent },
            ]
            break
        case 'loc':
            defaultRoles = [
                ...defaultRoles,
                { name: Roles.GroupLevel, parent: parseId(parent).parent },
                { name: Roles.CompanyLevel, parent },
                { name: Roles.LocationLevel, parent: _parent },
            ]
            break
        case 'usr':
            defaultRoles = [...defaultRoles, { name: Roles.UserLevel, parent: _parent }]
            break
        // case 'emp':
        //     defaultRoles = [...defaultRoles, { name: Roles.ServiceProviderLevel, parent: _parent }]
        //     break
    }
    return type === 'grp' || !parent ? defaultRoles : getDefaultRoles(parent, defaultRoles)
}

const getRolesToCheck = (
    _accessRoles: AccessRoles,
    parent?: string | null,
    parents: AccessRoleParents = {}
): UserRole[] => {
    const accessRoles: AccessRole[] = (() => {
        return coerceArray(_accessRoles)
            .filter(Boolean)
            .map((accessRole:any) => {
                if (typeof accessRole === 'string') {
                    const level = parseId(parent).type as AccessRole['level']
                    const parentType = findParentType(level)
                    return {
                        name: accessRole,
                        parent: parentType ? parents[parentType] || parent : parent,
                        level,
                    }
                }
                if (accessRole.level && !accessRole.parent) {
                    const parentType = findParentType(accessRole.level)
                    return {
                        ...accessRole,
                        parent: parentType ? parents[parentType] || parent : parent,
                    }
                }
                if (accessRole.parent && !accessRole.level) {
                    return {
                        ...accessRole,
                        level: parseId(accessRole.parent).type as AccessRole['level'],
                    }
                }
                return accessRole
            })
            .filter((role:any) => role.name && role.parent)
    })()
    return uniqBy(
        accessRoles.reduce<UserRole[]>(
            (allRoles, role) => [...allRoles, role, ...getDefaultRoles(role.parent!, [])],
            parent ? [...getDefaultRoles(parent, [])] : []
        ),
        (value) => [value.name, value.parent].join('---')
    )
}

export function can(
    roles: AccessRoles,
    parent: string | null = null,
    userRoles: UserRole[],
    parents: AccessRoleParents = {}
): boolean {
    const rolesToCheck: UserRole[] = getRolesToCheck(roles, parent, parents)
    const exist = rolesToCheck.find((r) =>
        // userRoles.find((u) => u.name === r.name && r.parent.includes(u.parent))
        userRoles.find((u) => u.name === r.name) // removed parent check for show if loc level
    )
    return Boolean(exist)
}

const getParents: (config: Config, parent?: string | null) => AccessRoleParents = (
    config,
    parent
) => {
    const { groupId, companyId, locationId, employeeId } = config
    if (!parent) return { groupId, companyId, locationId, employeeId }
    let parents: Record<string, string> = {}
    let p = parent
    for (; !!p; ) {
        const { parent: _parent, type } = parseId(p)
        if (type) {
            const parentType = findParentType(type as any)
            if (parentType) {
                parents[parentType] = p
            }
        }
        p = _parent
    }

    return {
        ...parents,
    }
}

export function canAccess(c: Config, roles: AccessRoles, parent?: string | null) {
    const { userRoles = [], ...config } = c
    return can(roles, parent, userRoles, getParents(config, parent))
}

const useCan = () => {
    const { userRoles = [], ...config } = useConfig()
    return (roles: AccessRoles, parent?: string | null) =>
        can(roles, parent, userRoles, getParents(config, parent))
}

export default useCan
